import { useState } from 'react';
import { toISODateString } from '@chainflip/utils/date';
import { isNotNullish } from '@chainflip/utils/guard';
import { type UTCDate } from '@date-fns/utc';
import { useMutation } from '@tanstack/react-query';
import { DashboardModalWithCloseButton, ModalContent } from '@/shared/components/dashboards/Modal';
import Button from '@/shared/components/flip-ui-kit/Button';
import { PeriodSelector } from '@/shared/components/PeriodSelector';
import { processorClient } from '@/shared/graphql/client';
import { getFundingEventsQuery } from '@/shared/graphql/explorer/account';
import {
  getHistoricalValidators,
  getOperatorDelegationActivitiesQuery,
  getOperatorRewardsHistoryQuery,
} from '@/shared/graphql/explorer/delegation';
import useToast from '@/shared/hooks/useToast';
import { timeframes } from '@/shared/utils/date';
import { exportXlsx, type Sheet } from '@/shared/utils/exportXlsx';
import { fetchAllPages } from '@/shared/utils/fetchAllPages';
import TokenAmount from '@/shared/utils/TokenAmount';

type TransactionType = 'Slashing' | 'Stake' | 'Delegation' | 'Undelegation' | 'Rewards accrued';

type HistoryRow = {
  'Operator ID': string;
  Timestamp: string;
  'Transaction Type': TransactionType;
  'Amount (FLIP)': number;
  'USD Value': number | '';
  'Validator ID': string;
};

const toUsdValue = (valueUsd: string | null | undefined): number | '' =>
  isNotNullish(valueUsd) ? Number(Number(valueUsd).toFixed(2)) : '';

const allTimeTimeframe = timeframes[timeframes.length - 1];

export default function ExportHistoryModal({
  operatorIdSs58,
  open,
  onClose,
}: {
  operatorIdSs58: string;
  open: boolean;
  onClose: () => void;
}) {
  const toast = useToast('export-operator-history');
  const [startDate, setStartDate] = useState<UTCDate>(allTimeTimeframe.startDate);
  const [endDate, setEndDate] = useState<UTCDate>(allTimeTimeframe.endDate);
  const [selectedDates, setSelectedDates] = useState<UTCDate[]>([
    allTimeTimeframe.startDate,
    allTimeTimeframe.endDate,
  ]);

  const { mutate: exportHistory, isPending } = useMutation({
    mutationFn: async () => {
      const isInRange = (timestamp: string) => {
        const time = new Date(timestamp).getTime();
        return time >= startDate.getTime() && time <= endDate.getTime();
      };

      const { links } = await processorClient.request(getHistoricalValidators, {
        idSs58: operatorIdSs58,
      });
      const validatorIds = links?.nodes.map((link) => link.validator.account.idSs58) ?? [];

      const [fundingEvents, delegationActivities, operatorEpochs] = await Promise.all([
        validatorIds.length === 0
          ? []
          : fetchAllPages(async (first, offset) => {
              const data = await processorClient.request(getFundingEventsQuery, {
                idSs58s: validatorIds,
                first,
                offset,
              });

              return {
                nodes: data.events?.edges.map((edge) => edge.node) ?? [],
                totalCount: data.events?.totalCount ?? 0,
              };
            }),
        fetchAllPages(async (first, offset) => {
          const data = await processorClient.request(getOperatorDelegationActivitiesQuery, {
            idSs58: operatorIdSs58,
            first,
            offset,
          });

          return {
            nodes: data.allDelegationActivities?.nodes ?? [],
            totalCount: data.allDelegationActivities?.totalCount ?? 0,
          };
        }),
        fetchAllPages(async (first, offset) => {
          const data = await processorClient.request(getOperatorRewardsHistoryQuery, {
            idSs58: operatorIdSs58,
            first,
            offset,
          });

          return {
            nodes: data.allOperatorEpoches?.nodes ?? [],
            totalCount: data.allOperatorEpoches?.totalCount ?? 0,
          };
        }),
      ]);

      const fundingRows = fundingEvents
        .filter(
          (event) =>
            (event.type === 'FUNDED' ||
              event.type === 'FUNDED_ON_CHAIN' ||
              event.type === 'SLASHED') &&
            isInRange(event.event.block.timestamp),
        )
        .map<HistoryRow>((event) => ({
          'Operator ID': operatorIdSs58,
          Timestamp: event.event.block.timestamp,
          'Transaction Type': event.type === 'SLASHED' ? 'Slashing' : 'Stake',
          'Amount (FLIP)': Number(TokenAmount.fromAsset(event.amount, 'Flip').toFixed()),
          'USD Value': toUsdValue(event.valueUsd),
          'Validator ID': event.account.idSs58,
        }));

      const delegationRows = delegationActivities
        .filter(
          (activity) =>
            (activity.type === 'DELEGATE' ||
              activity.type === 'UNDELEGATE' ||
              activity.type === 'BID_CHANGE') &&
            isInRange(activity.timestamp),
        )
        .map<HistoryRow>((activity) => {
          const amount = TokenAmount.fromAsset(activity.amount, 'Flip');
          let type: TransactionType;
          if (activity.type === 'DELEGATE') {
            type = 'Delegation';
          } else if (activity.type === 'UNDELEGATE') {
            type = 'Undelegation';
          } else {
            type = amount.value.isNegative() ? 'Undelegation' : 'Delegation';
          }

          return {
            'Operator ID': operatorIdSs58,
            Timestamp: activity.timestamp,
            'Transaction Type': type,
            'Amount (FLIP)': Number(amount.toFixed()),
            'USD Value': toUsdValue(activity.valueUsd),
            'Validator ID': '',
          };
        });

      const rewardRows = operatorEpochs
        .filter(
          ({ epoch, operatorRewardAmount }) =>
            isNotNullish(epoch.endBlock) &&
            isInRange(epoch.endBlock.timestamp) &&
            TokenAmount.fromAsset(operatorRewardAmount, 'Flip').gt(0),
        )
        .map<HistoryRow>(({ epoch, operatorRewardAmount, operatorRewardValueUsd }) => ({
          'Operator ID': operatorIdSs58,
          Timestamp: epoch.endBlock!.timestamp,
          'Transaction Type': 'Rewards accrued',
          'Amount (FLIP)': Number(TokenAmount.fromAsset(operatorRewardAmount, 'Flip').toFixed()),
          'USD Value': toUsdValue(operatorRewardValueUsd),
          'Validator ID': '',
        }));

      const sheet: Sheet = {
        name: 'Operator History',
        headers: [
          'Operator ID',
          'Timestamp',
          'Transaction Type',
          'Amount (FLIP)',
          'USD Value',
          'Validator ID',
        ],
        rows: [...fundingRows, ...delegationRows, ...rewardRows].sort(
          (a, b) => new Date(b.Timestamp).getTime() - new Date(a.Timestamp).getTime(),
        ),
      };

      exportXlsx(
        `${operatorIdSs58}-history-${toISODateString(startDate)}-${toISODateString(endDate)}.xlsx`,
        [sheet],
      );
    },
    onSuccess: onClose,
    onError: () => {
      toast.error('Failed to export history', {
        description: 'Something went wrong while fetching the history. Please try again.',
      });
    },
  });

  return (
    <DashboardModalWithCloseButton open={open} onClose={onClose}>
      <ModalContent
        title="Export history"
        description="Download this Operator's staking, delegation, slashing and rewards history as an Excel file."
        buttons={
          <Button
            onClick={() => exportHistory()}
            loading={isPending && 'iconOnly'}
            disabled="ifLoading"
          >
            Export
          </Button>
        }
      >
        <div className="flex flex-col gap-y-2">
          <span className="font-aeonikMedium text-14 text-cf-light-3">Timeframe</span>
          <PeriodSelector
            startDate={startDate}
            endDate={endDate}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
            selectedDates={selectedDates}
            setSelectedDates={setSelectedDates}
            buttonSize="medium"
          />
        </div>
      </ModalContent>
    </DashboardModalWithCloseButton>
  );
}
